import React, { useState, useEffect } from "react";

const C = {
  bg:"var(--bg)", card:"var(--bg-surface)", card2:"var(--bg-elevated)",
  border:"var(--border)", text:"var(--text-main)", muted:"var(--text-muted)",
  accent:"var(--accent)", green:"var(--success)", red:"var(--danger)", purple:"var(--purple,#a78bfa)",
  gold:"var(--warning)",
};

const TIER_COLOR = {BRONZE:"#cd7f32",SILVER:"#c0c0c0",GOLD:C.gold,PLATINUM:"var(--purple,#a78bfa)"};

export default function AffiliateDashboard() {
  const [overview, setOverview] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [tab, setTab] = useState("referrals");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
  const h = { Authorization:`Bearer ${token}` };

  useEffect(()=>{
    fetch("/api/investor/affiliate/overview", {headers:h})
      .then(r=>r.json())
      .then(d=>{ if(d.ok) setOverview(d.affiliate); })
      .finally(()=>setLoading(false));
  },[]);

  useEffect(()=>{
    if(tab==="referrals" && referrals.length===0)
      fetch("/api/investor/affiliate/referrals", {headers:h})
        .then(r=>r.json()).then(d=>{ if(d.ok) setReferrals(d.referrals||[]); });
    if(tab==="payouts" && payouts.length===0)
      fetch("/api/investor/affiliate/payouts", {headers:h})
        .then(r=>r.json()).then(d=>{ if(d.ok) setPayouts(d.payouts||[]); });
  },[tab]);

  const copyLink = () => {
    navigator.clipboard.writeText(overview?.referralLink||"");
    setCopied(true);
    setTimeout(()=>setCopied(false), 2000);
  };

  const isFlatMode = !overview || overview.affiliateMode === 1;
  const commissionPct = overview?.commissionPct ?? 10;
  const isCustom = overview?.isCustomCommission;

  if(loading) return (
    <div style={{padding:"48px 0",maxWidth:1200,margin:"0 auto",textAlign:"center",color:"var(--text-muted)"}}>Loading...</div>
  );

  if(overview && overview.enabled===false) return (
    <div style={{ color: "var(--text-main)", padding: "24px 0", maxWidth: 1200, margin: "0 auto" }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-main)", margin: 0 }}>Affiliate Program</h1>
      <div style={{background:C.card,borderRadius:12,padding:"20px 24px",border:`1px solid ${C.border}`,marginTop:16,fontSize:14,color:"var(--text-muted)"}}>
        The affiliate program is not available in {overview.communityName||"this community"} yet.
      </div>
    </div>
  );

  return (
    <div style={{ color: "var(--text-main)", padding: "24px 0", maxWidth: 1200, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>

      {/* Header */}
      <div style={{marginBottom: 0}}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-main)", margin: 0 }}>Affiliate Program</h1>
        <div style={{fontSize:13,color:"var(--text-muted)"}}>
          Earn commission by referring traders, analysts, and investors to {overview?.isPartner ? (overview.communityName||"our community") : "CrunchAlpha"}.
        </div>
      </div>

      {/* Commission badge + referral link */}
      <div style={{background:C.card,borderRadius:16,padding:"20px 24px",
        border:`1px solid ${C.accent}33`,marginBottom:20,
        display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:16}}>
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <div style={{width:56,height:56,borderRadius:12,
            background:"rgba(78,154,241,0.12)",border:`2px solid ${C.accent}`,
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:22,fontWeight:900,color:"var(--accent)"}}>
            {commissionPct}%
          </div>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:"var(--text-main)"}}>
              Your Commission Rate
              {isCustom && (
                <span style={{marginLeft:8,fontSize:10,padding:"2px 7px",borderRadius:4,
                  background:"rgba(167,139,250,0.15)",color:"var(--accent)",fontWeight:700}}>
                  CUSTOM
                </span>
              )}
            </div>
            <div style={{fontSize:12,color:"var(--text-muted)",marginTop:2}}>
              {isFlatMode
                ? "Flat rate — earn on every active referral"
                : `Tier: ${overview?.tier||"BRONZE"} · Next: ${overview?.nextTier} (${overview?.nextTierReq})`}
            </div>
          </div>
        </div>

        {/* Referral link */}
        <div style={{display:"flex",alignItems:"center",gap:8,flex:1,maxWidth:420}}>
          <div style={{flex:1,background:C.card2,borderRadius:8,padding:"8px 12px",
            fontSize:11,color:"var(--text-muted)",fontFamily:"monospace",
            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
            border:`1px solid ${C.border}`}}>
            {overview?.referralLink||"—"}
          </div>
          <button onClick={copyLink} style={{padding:"8px 16px",borderRadius:8,border:"none",
            cursor:"pointer",fontSize:12,fontWeight:700,flexShrink:0,
            background:copied?C.green:C.accent,color:"#fff",transition:"background 0.2s"}}>
            {copied?"Copied":"Copy Link"}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",
        gap:12,marginBottom:20}}>
        {[
          {l:"Total Referrals",  v:overview?.totalReferrals??0,   c:C.text,  s:"All time"},
          {l:"Active Referrals", v:overview?.activeReferrals??0,  c:C.green, s:"Currently active"},
          {l:"EUM",              v:`$${Number(overview?.eumUSD??0).toLocaleString()}`, c:C.accent, s:"Equiv. under mgmt"},
          {l:"Total Earned",     v:`$${Number(overview?.totalPayout??0).toFixed(2)}`,  c:C.green,  s:"Paid out"},
          {l:"Pending",          v:`$${Number(overview?.pendingPayout??0).toFixed(2)}`, c:C.gold,  s:"Awaiting payment"},
        ].map(x=>(
          <div key={x.l} style={{background:C.card,borderRadius:12,padding:"14px 16px",
            border:`1px solid ${C.border}`}}>
            <div style={{fontSize:10,color:"var(--text-muted)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.05em"}}>{x.l}</div>
            <div style={{fontSize:20,fontWeight:800,color:x.c}}>{x.v}</div>
            <div style={{fontSize:10,color:"var(--text-muted)",marginTop:3}}>{x.s}</div>
          </div>
        ))}
      </div>

      {/* Tier progression — only show if tier mode */}
      {!isFlatMode && (
        <div style={{background:C.card,borderRadius:12,padding:"16px 20px",
          border:`1px solid ${C.border}`,marginBottom:20}}>
          <div style={{fontSize:12,fontWeight:800,marginBottom:12,textTransform:"uppercase",letterSpacing:"0.05em"}}>Tier Progression</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
            {["BRONZE","SILVER","GOLD","PLATINUM"].map(t=>{
              const active = overview?.tier===t;
              const tc = TIER_COLOR[t];
              return (
                <div key={t} style={{textAlign:"center",padding:"10px 8px",borderRadius:8,
                  background:active?`${tc}18`:"rgba(255,255,255,0.02)",
                  border:`1px solid ${active?tc:C.border}`}}>
                  <div style={{fontSize:11,fontWeight:800,color:active?tc:C.muted,marginBottom:2}}>{t}</div>
                  <div style={{fontSize:16,fontWeight:900,color:active?tc:C.muted}}>
                    {{BRONZE:3,SILVER:5,GOLD:7,PLATINUM:10}[t]}%
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{fontSize:11,color:"var(--text-muted)",marginTop:12,lineHeight:1.6}}>
            Bronze: &lt;5 referrals · Silver: 5+ referrals + $5k EUM · Gold: 20+ + $50k EUM · Platinum: $150k+ EUM
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        {[["referrals","Referrals"],["payouts","Payouts"],["howto","How It Works"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{
            padding:"7px 18px",borderRadius:8,border:"none",cursor:"pointer",
            fontSize:12,fontWeight:700,
            background:tab===id?C.accent:"rgba(255,255,255,0.05)",
            color:tab===id?"#fff":C.muted}}>
            {label}
          </button>
        ))}
      </div>

      {/* Referrals tab */}
      {tab==="referrals" && (
        <div style={{background:C.card,borderRadius:12,border:`1px solid ${C.border}`}}>
          {referrals.length===0 ? (
            <div style={{textAlign:"center",padding:"48px 24px",color:"var(--text-muted)"}}>
              <div style={{fontSize:14,fontWeight:700,color:"var(--text-main)",marginBottom:8}}>No referrals yet</div>
              <div style={{fontSize:12,marginBottom:16}}>Share your referral link to start earning.</div>
              <button onClick={copyLink} style={{padding:"9px 20px",borderRadius:8,
                background:C.accent,color:"#fff",border:"none",cursor:"pointer",
                fontSize:12,fontWeight:700}}>
                Copy Referral Link
              </button>
            </div>
          ) : (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:`1px solid ${C.border}`}}>
                    {["Email","Role","Status","Joined"].map(h=>(
                      <th key={h} style={{padding:"10px 16px",textAlign:"left",
                        fontWeight:600,fontSize:11,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.05em"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((r,i)=>(
                    <tr key={i} style={{borderBottom:`1px solid rgba(255,255,255,0.03)`}}>
                      <td style={{padding:"10px 16px",fontFamily:"monospace",fontSize:12}}>{r.email}</td>
                      <td style={{padding:"10px 16px",color:"var(--text-muted)",fontSize:11,textTransform:"capitalize"}}>{r.role||"—"}</td>
                      <td style={{padding:"10px 16px"}}>
                        <span style={{padding:"2px 8px",borderRadius:4,fontSize:10,fontWeight:700,
                          background:r.status==="ACTIVE"?"rgba(29,209,161,0.15)":"rgba(248,113,113,0.15)",
                          color:r.status==="ACTIVE"?C.green:C.red}}>{r.status}</span>
                      </td>
                      <td style={{padding:"10px 16px",color:"var(--text-muted)",fontSize:11}}>{r.createdAt?.slice(0,10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Payouts tab */}
      {tab==="payouts" && (
        <div style={{background:C.card,borderRadius:12,border:`1px solid ${C.border}`}}>
          {payouts.length===0 ? (
            <div style={{textAlign:"center",padding:"48px 24px",color:"var(--text-muted)"}}>
              <div style={{fontSize:14,fontWeight:700,color:"var(--text-main)",marginBottom:8}}>No payouts yet</div>
              <div style={{fontSize:12}}>Payouts are processed monthly based on your active referrals.</div>
            </div>
          ) : (
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:`1px solid ${C.border}`}}>
                    {["Amount","Source","Period","Status","Date"].map(h=>(
                      <th key={h} style={{padding:"10px 16px",textAlign:"left",
                        fontWeight:600,fontSize:11,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.05em"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p,i)=>(
                    <tr key={i} style={{borderBottom:`1px solid rgba(255,255,255,0.03)`}}>
                      <td style={{padding:"10px 16px",fontWeight:800,color:"var(--success)"}}>${p.amount?.toFixed(2)}</td>
                      <td style={{padding:"10px 16px",color:"var(--text-muted)",fontSize:11,textTransform:"capitalize"}}>{p.source?.replace(/_/g," ")}</td>
                      <td style={{padding:"10px 16px",color:"var(--text-muted)",fontSize:11}}>{p.period||"—"}</td>
                      <td style={{padding:"10px 16px"}}>
                        <span style={{padding:"2px 8px",borderRadius:4,fontSize:10,fontWeight:700,
                          background:p.status==="PAID"?"rgba(29,209,161,0.15)":"rgba(251,191,36,0.15)",
                          color:p.status==="PAID"?C.green:C.gold}}>{p.status}</span>
                      </td>
                      <td style={{padding:"10px 16px",color:"var(--text-muted)",fontSize:11}}>{p.createdAt?.slice(0,10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* How it works tab */}
      {tab==="howto" && (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {[
            {n:"1",title:"Share your referral link",
              text:"Copy your unique referral link and share it. When someone registers using your link, they become your referral."},
            {n:"2",title:"They become active",
              text:"Your referrals connect their broker accounts, copy traders, or subscribe to signal sets."},
            {n:"3",title:"You earn commission",
              text:`You earn ${commissionPct}% of the platform revenue generated by your referrals — including performance fees, subscription fees, and rebates.`},
            {n:"4",title:"Monthly payouts",
              text:"Commissions are calculated monthly and paid out to your registered payment method."},
          ].map(s=>(
            <div key={s.n} style={{background:C.card,borderRadius:12,padding:"16px 20px",
              border:`1px solid ${C.border}`,display:"flex",gap:16,alignItems:"flex-start"}}>
              <div style={{width:32,height:32,borderRadius:8,background:"rgba(78,154,241,0.12)",
                border:`1px solid ${C.accent}44`,display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:14,fontWeight:900,color:"var(--accent)",flexShrink:0}}>{s.n}</div>
              <div>
                <div style={{fontSize:13,fontWeight:800,marginBottom:4}}>{s.title}</div>
                <div style={{fontSize:12,color:"var(--text-muted)",lineHeight:1.7}}>{s.text}</div>
              </div>
            </div>
          ))}
          <div style={{background:"rgba(248,113,113,0.06)",borderRadius:12,padding:"14px 20px",
            border:"1px solid rgba(248,113,113,0.15)",fontSize:12,color:"var(--text-muted)",lineHeight:1.7}}>
            <strong style={{color:"var(--danger)"}}>Anti-abuse policy:</strong> Fake accounts, circular referrals,
            and immediate churn are automatically detected and will result in referral invalidation.
            Only genuine, active referrals count toward your commission.
          </div>
        </div>
      )}

      {/* HOW TO PROMOTE */}
      <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:12,padding:24,marginTop:20}}>
        <div style={{fontSize:15,fontWeight:800,color:"var(--text-main)",marginBottom:4}}>Promote Strategies & Earn</div>
        <div style={{fontSize:12,color:"var(--text-muted)",marginBottom:20,lineHeight:1.7}}>
          Share any strategy from the marketplace using your unique referral link.
          When someone registers and connects their broker, you earn a commission on their performance fees.
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12,marginBottom:20}}>
          {[
            { step:"1", title:"Copy your referral link", desc:"Your unique link is shown above. Every registration via your link is tracked automatically." },
            { step:"2", title:"Browse the Marketplace", desc:"Find strategies with strong AlphaScore and track record. These convert better with your audience." },
            { step:"3", title:"Click Share & Earn", desc:"Each strategy card has a Share & Earn button. It generates a link with your ref code attached." },
            { step:"4", title:"Share to your audience", desc:"Post to WhatsApp, Telegram, Instagram, or any channel. The link shows a live preview of the strategy." },
          ].map(s=>(
            <div key={s.step} style={{background:"var(--bg-elevated)",borderRadius:8,padding:"14px 16px"}}>
              <div style={{width:26,height:26,borderRadius:"50%",background:"var(--accent-soft)",
                border:"1px solid var(--accent-border)",display:"flex",alignItems:"center",
                justifyContent:"center",fontSize:12,fontWeight:700,color:"var(--accent)",marginBottom:10}}>
                {s.step}
              </div>
              <div style={{fontSize:12,fontWeight:700,color:"var(--text-main)",marginBottom:4}}>{s.title}</div>
              <div style={{fontSize:11,color:"var(--text-muted)",lineHeight:1.6}}>{s.desc}</div>
            </div>
          ))}
        </div>
        <div style={{background:"rgba(78,154,241,0.07)",border:"1px solid rgba(78,154,241,0.2)",borderRadius:8,padding:"12px 16px",
          display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
          <div>
            <div style={{fontSize:12,fontWeight:700,color:"var(--accent)",marginBottom:2}}>Quick tip</div>
            <div style={{fontSize:11,color:"var(--text-muted)"}}>
              When you share a strategy link, WhatsApp & Telegram automatically show a preview card
              with the trader's name, AlphaScore, ROI, and risk level — no design work needed.
            </div>
          </div>
          <a href="/marketplace"
            style={{padding:"8px 18px",borderRadius:8,background:"var(--accent)",color:"#fff",
              fontSize:12,fontWeight:700,textDecoration:"none",whiteSpace:"nowrap",flexShrink:0}}>
            Browse Marketplace →
          </a>
        </div>
      </div>

    </div>
  );
}
